import { useEffect, useState, JSX } from 'react';
import {
  Link,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  FormHelperText,
  Alert,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { IGroup, IUser } from 'common';
import { Admin } from '../Admin';
import api from '../../services/api';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '../../styles';


interface UserEditDialogProps {
  onValidItem: (user: Partial<IUser>) => void;
  user: Partial<IUser> | null;
  groups: IGroup[];
}

function UserEditDialog(props: UserEditDialogProps) {
  const [email, setEmail] = useState(props.user?.email || '');
  const [groupName, setGroupName] = useState(props.user?.group?.name || '');
  const [availableGroups, setAvailableGroups] = useState<IGroup[]>(props.user?.availableGroups || []);
  const [catalogCountries, setCatalogCountries] = useState<string[]>(props.user?.catalogCountries || []);
  const [availableCatalogCountries, setAvailableCatalogCountries] = useState<string[]>([]);

  // Reset form when props.user changes (when opening dialog for different user)
  useEffect(() => {
    setEmail(props.user?.email || '');
    setGroupName(props.user?.group?.name || '');
    
    // Match saved groups with current groups list by _id
    // Backend returns availableGroups as array of string IDs, not full objects
    if (props.user?.availableGroups && props.user.availableGroups.length > 0 && props.groups.length > 0) {
      const savedGroupIds = props.user.availableGroups;
      
      // Match IDs with full group objects from props.groups
      const matchedGroups = savedGroupIds
        .map(groupIdOrObject => {
          // Handle both string IDs and potential objects
          const groupId = typeof groupIdOrObject === 'string' 
            ? groupIdOrObject 
            : (groupIdOrObject as IGroup)._id;
          return props.groups.find(g => g._id === groupId);
        })
        .filter(Boolean) as IGroup[];
      
      setAvailableGroups(matchedGroups);
    } else {
      setAvailableGroups([]);
    }
    
    setCatalogCountries(props.user?.catalogCountries || []);
  }, [props.user, props.groups]);

  useEffect(() => {
    // Update available catalogCountries when group changes
    const selectedGroup = props.groups.find((group) => group.name === groupName);
    const groupCatalogCountries = selectedGroup?.catalogCountries || [];
    setAvailableCatalogCountries(groupCatalogCountries); // Show all group catalogCountries as options
  }, [groupName, props.groups]);

  const setUserValidity = () => {
    if (email.length > 1 && groupName.length > 1) {
      const selectedGroup = props.groups.find((group) => group.name === groupName);

      const newUser: Partial<IUser> = {
        ...props.user,
        email,
        group: selectedGroup || props.user?.group,
        availableGroups,
        catalogCountries,
      };

      props.onValidItem(newUser);
    }
  };

  useEffect(setUserValidity, [email, groupName, availableGroups, catalogCountries]);

  return (
    <Box className={form} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        type="email"
        value={email}
        label="Email"
        placeholder="john.doe@example.com"
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
          },
        }}
      />
      <FormControl fullWidth>
        <InputLabel>Group Name</InputLabel>
        <Select 
          label="Group Name" 
          value={groupName} 
          onChange={(e) => setGroupName(e.target.value as string)}
          sx={{
            borderRadius: '20px',
          }}
        >
          {props.groups.map((group) => (
            <MenuItem 
              value={group.name} 
              key={group.name}
              sx={{
                '&.Mui-selected': {
                  bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  '&:hover': {
                    bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  }
                }
              }}
            >
              {group.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>Primary group for this user</FormHelperText>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Available groups (multiple)</InputLabel>
        <Select 
          label="Available groups (multiple)" 
          value={availableGroups.map(g => g._id)}
          multiple 
          onChange={(e) => {
            const selectedIds = e.target.value as string[];
            const selectedGroups = props.groups.filter(g => selectedIds.includes(g._id));
            setAvailableGroups(selectedGroups);
          }}
          renderValue={() => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {availableGroups.map((group) => (
                <Chip 
                  key={group._id || group.name} 
                  label={group.name} 
                  size="small"
                  sx={{ 
                    height: '24px',
                  }}
                />
              ))}
            </Box>
          )}
          sx={{
            borderRadius: '20px',
          }}
        >
          {props.groups.map((group) => (
            <MenuItem 
              value={group._id} 
              key={group._id}
              sx={{
                '&.Mui-selected': {
                  bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  '&:hover': {
                    bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  }
                }
              }}
            >
              {group.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>User can switch between these groups</FormHelperText>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Catalog countries (multiple)</InputLabel>
        <Select
          label="Catalog countries (multiple)"
          value={catalogCountries}
          multiple
          onChange={(e) => setCatalogCountries(e.target.value as string[])}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((country) => (
                <Chip 
                  key={country} 
                  label={country} 
                  size="small"
                  sx={{ 
                    height: '24px',
                  }}
                />
              ))}
            </Box>
          )}
          sx={{
            borderRadius: '20px',
          }}
        >
          {availableCatalogCountries.map((localization) => (
            <MenuItem 
              value={localization} 
              key={localization}
              sx={{
                '&.Mui-selected': {
                  bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  '&:hover': {
                    bgcolor: ADALONG_COLORS.FLASH_BLUE,
                  }
                }
              }}
            >
              {localization}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>Available product catalog regions</FormHelperText>
      </FormControl>
    </Box>
  );
}

const BOOKMARKS_KEY = 'userBookmarks';
const INFO_NOTE_KEY = 'usersInfoNoteDismissed';

function Users() {
  const [bookmarkedUsers, setBookmarkedUsers] = useState<Partial<IUser>[]>(() => {
    const stored = localStorage.getItem(BOOKMARKS_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to load bookmarks:', e);
      }
    }
    return [];
  });
  const [showInfoNote, setShowInfoNote] = useState(() => {
    return !localStorage.getItem(INFO_NOTE_KEY);
  });

  // Save bookmarks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarkedUsers));
  }, [bookmarkedUsers]);

  const toggleBookmark = (user: Partial<IUser>) => {
    setBookmarkedUsers((prev) => {
      const exists = prev.find((u) => u._id === user._id);
      if (exists) {
        return prev.filter((u) => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const isBookmarked = (user: Partial<IUser>): boolean => {
    return bookmarkedUsers.some((u) => u._id === user._id);
  };

  const handleDismissInfoNote = () => {
    localStorage.setItem(INFO_NOTE_KEY, 'true');
    setShowInfoNote(false);
  };

  const displayHeader = (
    <TableRow>
      <TableCell key="0" sx={{ width: '80px', textAlign: 'center' }}>AdAlong</TableCell>
      <TableCell key="1">Email</TableCell>
      <TableCell key="2">Group</TableCell>
      <TableCell key="3">Creation Date</TableCell>
      <TableCell key="4">Extension API Key</TableCell>
      <TableCell key="5">Login</TableCell>
      <TableCell key="6" />
    </TableRow>
  );
  const [groups, setGroups] = useState<IGroup[]>([]);

  useEffect(() => {
    api
      .getGroups({
        filters: {
          name: '',
        },
        options: {
          limit: 1000,
          skip: 0,
        },
      })
      .then(({ groups }) => setGroups(groups))
      .catch((e) => {
        console.error('Error loading groups:', e);
      });
  }, []);

  const create = (user: Partial<IUser>): Promise<void> => {
    return api.createUser(user).then();
  };

  const update = (user: Partial<IUser>): Promise<void> => {
    if (!user._id) {
      return Promise.reject(new Error('Cannot update user without an _id'));
    }
    return api.updateUser(user._id!, user).then();
  };

  const search = (input: string): Promise<Partial<IUser>[]> => {
    // limit: 300, no pagination for the moment.
    // It should be reworked when the identity system will be developed.
    return api.getUsers({ filters: { name: input }, options: { limit: 300, skip: 0 } }).then((res) => {
      // Sort @adalong.com users first
      const users = res.users;
      return users.sort((a, b) => {
        const aIsAdalong = a.email?.endsWith('@adalong.com') || false;
        const bIsAdalong = b.email?.endsWith('@adalong.com') || false;
        if (aIsAdalong && !bIsAdalong) return -1;
        if (!aIsAdalong && bIsAdalong) return 1;
        return 0;
      });
    });
  };

  const remove = (user: Partial<IUser>): Promise<void> => {
    if (!user._id) {
      return Promise.reject(new Error('Cannot delete user without an _id'));
    }
    return api.removeUser(user._id).then();
  };

  const logAs = (user: Partial<IUser>): void => {
    if (!user._id) {
      throw new Error('Cannot log as user without an _id');
    }
    api.logAsUser(user._id).then((url) => {
      window.open(url, 'adalongasadmin');
    });
  };

  const displayLine = (user: Partial<IUser>): JSX.Element[] => {
    const isAdalong = user.email?.endsWith('@adalong.com');
    const bookmarked = isBookmarked(user);
    return [
      <TableCell key="0" sx={{ textAlign: 'center' }}>
        {isAdalong && (
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#0005DC',
              display: 'inline-block',
            }}
          />
        )}
      </TableCell>,
      <TableCell key="1">{user.email}</TableCell>,
      <TableCell key="2">{user.group?.name}</TableCell>,
      <TableCell key="3">{user.created_at ? new Date(user.created_at).toLocaleString() : ''}</TableCell>,
      <TableCell key="4">{user.extensionApiKey}</TableCell>,
      <TableCell key="5">
        <Link className={iconLink} onClick={() => logAs(user)} sx={{ cursor: 'pointer', color: '#ffffff', textDecoration: 'none' }}>
          <LogoutIcon sx={{ color: 'inherit' }} />
        </Link>
      </TableCell>,
      <TableCell key="bookmark">
        <IconButton onClick={() => toggleBookmark(user)} size="small" sx={{ color: ADALONG_COLORS.LIGHT_WHITE }}>
          {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
        </IconButton>
      </TableCell>,
    ];
  };

  const displayEditWindow = (user: Partial<IUser> | null, onValidItem: (user: Partial<IUser>) => void): JSX.Element => {
    return <UserEditDialog onValidItem={onValidItem} user={user} groups={groups} />;
  };

  const renderBookmarks = () => {
    if (bookmarkedUsers.length === 0) return null;

    return (
      <Box mb={3}>
        <Typography variant="h6" sx={{ mb: 2, color: ADALONG_COLORS.LIGHT_WHITE }}>
          My user bookmarks
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {bookmarkedUsers.map((user) => (
            <Card
              key={user._id}
              sx={{
                bgcolor: ADALONG_COLORS.LIGHT_BLACK,
                color: ADALONG_COLORS.LIGHT_WHITE,
                borderRadius: '12px',
                minWidth: '280px',
                border: `1px solid ${ADALONG_COLORS.LIGHT_BLACK}`,
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {user.email}
                  </Typography>
                  <Typography variant="body2" sx={{ color: ADALONG_COLORS.LIGHT_GRAY }}>
                    {user.group?.name}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => logAs(user)}
                  size="small"
                  sx={{ color: ADALONG_COLORS.LIGHT_WHITE }}
                  title="Login as user"
                >
                  <LogoutIcon />
                </IconButton>
                <IconButton
                  onClick={() => toggleBookmark(user)}
                  size="small"
                  sx={{ color: ADALONG_COLORS.FLASH_BLUE }}
                  title="Remove bookmark"
                >
                  <BookmarkIcon />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  };

  const renderInfoNote = () => {
    if (!showInfoNote) return null;

    return (
      <Box mb={3}>
        <Alert
          variant="filled"
          onClose={handleDismissInfoNote}
          icon={false}
          sx={{
            bgcolor: ADALONG_COLORS.LIGHT_BLACK,
            color: ADALONG_COLORS.LIGHT_WHITE,
            borderRadius: '10px',
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            User Management Options
          </Typography>
          <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
            <li>Bookmark users: Click the bookmark icon to save frequently accessed users for quick access</li>
            <li>Visual help: AdAlong users are marked with a blue dot and automatically sorted to appear at the top of the list</li>
          </Typography>
        </Alert>
      </Box>
    );
  };

  return (
    <Box>
      <Admin<Partial<IUser>>
        title="Users"
        displayHeader={displayHeader}
        create={create}
        update={update}
        search={search}
        remove={remove}
        getId={(user) => user._id!}
        displayLine={displayLine}
        displayEditWindow={displayEditWindow}
        customSection={
          <>
            {renderInfoNote()}
            {renderBookmarks()}
          </>
        }
      />
    </Box>
  );
}

export default Users;

const form = style({ display: 'flex', flexDirection: 'column', gap: 10 });
const iconLink = style({ padding: '0 6px', cursor: 'pointer' });
